'use client';

import React, { useRef, useMemo, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import MobileBrandVisibilityMetrics from './MobileBrandVisibilityMetrics';

// Register plugins
if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const GrowthImpactSection = () => {
    // Refs for Section
    const sectionRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const headingRef = useRef<HTMLDivElement>(null);
    const labelRef = useRef<HTMLDivElement>(null);
    const subtitleRef = useRef<HTMLDivElement>(null);

    // Card Refs
    const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

    // Chart Refs & Data
    const donutRef = useRef<SVGSVGElement>(null);
    const donutSegmentsRef = useRef<(SVGPathElement | null)[]>([]);
    const donutCenterNumRef = useRef<HTMLSpanElement>(null);
    const donutLabelsRef = useRef<HTMLDivElement>(null);

    // BAR CHART REFS
    const barWidgetRef = useRef<HTMLDivElement>(null);
    const barTitleRef = useRef<HTMLHeadingElement>(null);
    const barBadgeRef = useRef<HTMLDivElement>(null);
    const barsRef = useRef<(HTMLDivElement | null)[]>([]);
    const barLabelsRef = useRef<(HTMLSpanElement | null)[]>([]);
    const barValuesRef = useRef<(HTMLSpanElement | null)[]>([]);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const rankNumRef = useRef<HTMLDivElement>(null);
    const rankPillRef = useRef<HTMLDivElement>(null);

    const linePathRef = useRef<SVGPathElement>(null);
    const lineAreaRef = useRef<SVGPathElement>(null);
    const pulseDotRef = useRef<SVGCircleElement>(null);

    // --- DATA ---
    const TRAFFIC_DATA = [
        { label: "Organic Search", value: 54, color: "#10b981" },
        { label: "GMB Profile", value: 31, color: "#3b82f6" },
        { label: "Direct/Other", value: 11, color: "#ec4899" },
        { label: "Social", value: 4, color: "#f59e0b" }
    ];

    // UPDATED: Credible Demo Data (Aligned with 54% of 2860)
    const BAR_DATA = [
        { label: "Aug", value: 620 },
        { label: "Sep", value: 850 },
        { label: "Oct", value: 1050 },
        { label: "Nov", value: 1280 },
        { label: "Dec", value: 1550 },
    ];

    // Normalize height for bar display (Max value = 100%)
    const maxBarValue = Math.max(...BAR_DATA.map(d => d.value));
    const normalizedBarData = BAR_DATA.map(d => ({
        ...d,
        height: `${(d.value / maxBarValue) * 100}%`
    }));

    // Auto-calculate Growth Badge
    const growthStats = useMemo(() => {
        const current = BAR_DATA[BAR_DATA.length - 1].value;
        const previous = BAR_DATA[BAR_DATA.length - 2].value;
        const growth = ((current - previous) / previous) * 100;
        return {
            percent: Math.round(growth),
            isPositive: growth >= 0
        };
    }, []);

    const LINE_DATA = [180, 240, 210, 290, 340, 360]; // Mon-Sat (Realistic Volume)

    // Helper: Generate Smooth Path
    const generatePath = (data: number[], width: number, height: number, close: boolean = false) => {
        const max = 400; // Increased max scale for new data
        const min = 0;
        const range = max - min;

        const points = data.map((val, i) => {
            const x = (i / (data.length - 1)) * width;
            const y = height - ((val - min) / range) * height;
            return [x, y];
        });

        let path = `M ${points[0][0]} ${points[0][1]}`;
        for (let i = 0; i < points.length; i++) {
            const p = points[i];
            if (i === 0) continue;
            const prev = points[i - 1];
            const cp1x = prev[0] + (p[0] - prev[0]) * 0.5;
            const cp1y = prev[1];
            const cp2x = prev[0] + (p[0] - prev[0]) * 0.5;
            const cp2y = p[1];
            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p[0]} ${p[1]}`;
        }
        if (close) {
            path += ` L ${width} ${height} L 0 ${height} Z`;
        }
        return path;
    };

    // Calculate Charts (Client-side only to avoid hydration mismatch)
    const [donutSegments, setDonutSegments] = useState<any[]>([]);
    const [lineChartPaths, setLineChartPaths] = useState({ area: "", line: "" });

    React.useEffect(() => {
        // 1. Donut
        let cumulative = 0;
        const total = 100;
        const segments = TRAFFIC_DATA.map(item => {
            const startAngle = (cumulative / total) * 360;
            const endAngle = ((cumulative + item.value) / total) * 360;
            cumulative += item.value;

            const radius = 80;
            const cx = 100, cy = 100;
            const startRad = (startAngle - 90) * Math.PI / 180;
            const endRad = (endAngle - 90) * Math.PI / 180;

            const x1 = cx + radius * Math.cos(startRad);
            const y1 = cy + radius * Math.sin(startRad);
            const x2 = cx + radius * Math.cos(endRad);
            const y2 = cy + radius * Math.sin(endRad);

            const largeArc = item.value > 50 ? 1 : 0;

            return {
                ...item,
                d: `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`
            };
        });
        setDonutSegments(segments);

        // 2. Line Chart
        setLineChartPaths({
            area: generatePath(LINE_DATA, 500, 150, true),
            line: generatePath(LINE_DATA, 500, 150, false)
        });
    }, []);

    // Tooltip State
    const [tooltipContent, setTooltipContent] = useState({ month: '', value: 0 });

    useGSAP(() => {
        if (!containerRef.current) return;

        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReduced) {
            gsap.set([labelRef.current, headingRef.current, subtitleRef.current, ...cardsRef.current], { opacity: 1, y: 0, rotateX: 0 });
            return;
        }

        const masterTl = gsap.timeline({
            scrollTrigger: {
                trigger: sectionRef.current,
                start: "top 70%",
                once: true
            }
        });

        // 1. GLOBAL ENTRANCE
        masterTl.fromTo(labelRef.current,
            { opacity: 0, y: 10, scale: 0.98 },
            { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "power3.out" }
        )
            .fromTo([headingRef.current, subtitleRef.current],
                { opacity: 0, y: 24 },
                { opacity: 1, y: 0, duration: 0.8, stagger: 0.1, ease: "power3.out" },
                "-=0.4"
            )
            .fromTo(cardsRef.current,
                { opacity: 0, y: 30, rotateX: 6, transformPerspective: 800 },
                { opacity: 1, y: 0, rotateX: 0, duration: 0.8, stagger: 0.15, ease: "power3.out" },
                "-=0.5"
            );

        // 2. DONUT + RANK + LINE Specifics
        if (donutSegmentsRef.current.length) {
            masterTl.to(donutSegmentsRef.current, { scale: 1, duration: 0.6, stagger: 0.1, ease: "back.out(1.2)" }, "-=0.6");
        }
        if (donutCenterNumRef.current) {
            masterTl.fromTo(donutCenterNumRef.current, { innerText: "0" }, { innerText: "2860", snap: { innerText: 1 }, duration: 2, ease: "power2.out" }, "<");
        }
        if (donutLabelsRef.current) {
            masterTl.fromTo(donutLabelsRef.current.children, { opacity: 0, x: -10 }, { opacity: 1, x: 0, stagger: 0.1, duration: 0.5 }, "-=1.0");
        }

        // Rank Anim: Just Scale/Fade primarily, assuming static text for "Top 3" or simple animation
        if (rankNumRef.current) {
            masterTl.fromTo(rankNumRef.current,
                { opacity: 0, scale: 0.8 },
                { opacity: 1, scale: 1, duration: 0.8, ease: "back.out(1.5)" },
                "-=0.5"
            );
        }
        if (rankPillRef.current) masterTl.fromTo(rankPillRef.current, { x: 20, opacity: 0 }, { x: 0, opacity: 1, duration: 0.6, ease: "power3.out" }, "<+0.2");

        if (linePathRef.current) {
            const len = linePathRef.current.getTotalLength();
            gsap.set(linePathRef.current, { strokeDasharray: len, strokeDashoffset: len });
            masterTl.to(linePathRef.current, { strokeDashoffset: 0, duration: 1.5, ease: "power2.inOut" }, "-=0.8");
            masterTl.to(lineAreaRef.current, { opacity: 1, duration: 0.8 }, "-=0.8");
        }

        // ===============================================
        // PART 2: BAR CHART ENTRANCE (Specific Timeline)
        // ===============================================

        // Ensure values are numbers for snap
        barValuesRef.current.forEach(el => {
            if (el) el.innerText = "0";
        });

        const barTl = gsap.timeline({
            scrollTrigger: {
                trigger: barWidgetRef.current,
                start: "top 75%",
                once: true
            }
        });

        // 1) Title Animation
        barTl.fromTo(barTitleRef.current,
            { y: 12, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" }
        )
            // 2) Badge Animation
            .fromTo(barBadgeRef.current,
                { opacity: 0, scale: 0.98 },
                { opacity: 1, scale: 1, duration: 0.4, ease: "back.out(1.5)" },
                "-=0.2"
            )
            // 3) Bars Animation (Staggered from bottom)
            .fromTo(barsRef.current,
                { scaleY: 0 },
                {
                    scaleY: 1,
                    duration: 0.6,
                    stagger: 0.1,
                    ease: "power3.out",
                    transformOrigin: "bottom"
                },
                "-=0.3"
            )
            // Overshoot effect
            .to(barsRef.current, {
                scaleY: 1.04,
                duration: 0.15,
                stagger: 0.1,
                ease: "power1.out",
                yoyo: true,
                repeat: 1
            }, "<+=0.4")

            // 4) Labels Fade In
            .fromTo(barLabelsRef.current,
                { opacity: 0, y: 6 },
                { opacity: 1, y: 0, duration: 0.4, stagger: 0.1 },
                "-=1.2" // Overlap with bars
            );

        // 5) Top Numbers Count Up (After each bar finishes)
        barValuesRef.current.forEach((el, i) => {
            if (!el) return;
            const realValue = BAR_DATA[i].value;
            barTl.to(el, {
                innerText: realValue,
                snap: { innerText: 1 },
                duration: 1,
                ease: "power2.out"
            }, `-=${1.5 - (i * 0.1)}`); // Sync roughly with bar rise
        });


        // ===============================================
        // PART 4: LIVE PULSE (Subtle)
        // ===============================================
        // Pulse the LAST bar (Dec) every 5-7 seconds

        const lastBar = barsRef.current[BAR_DATA.length - 1];
        if (lastBar) {
            const pulseTl = gsap.to(lastBar, {
                scaleY: 1.02,
                duration: 0.8,
                ease: "power1.inOut",
                yoyo: true,
                repeat: 1,
                repeatDelay: 5, // 5 seconds + animation time approx 6-7s cycle
                paused: true
            });

            // Start loop
            const pulseInterval = setInterval(() => {
                // Check hover state (using a class we will toggle)
                if (!lastBar.classList.contains('is-hovered')) {
                    pulseTl.restart();
                }
            }, 6000);

            return () => clearInterval(pulseInterval);
        }

    }, { scope: sectionRef });

    // --- INTERACTION HANDLERS ---

    // Hover logic for Bars
    const handleBarHover = (index: number, isHovering: boolean, e?: React.BaseSyntheticEvent) => {
        const bar = barsRef.current[index];
        if (!bar) return;

        if (isHovering) {
            bar.classList.add('is-hovered');
            gsap.to(bar, { scaleX: 1.02, y: -4, duration: 0.3, ease: "power2.out", overwrite: 'auto' });
            barsRef.current.forEach((b, i) => {
                if (i !== index && b) {
                    gsap.to(b, { opacity: 0.65, duration: 0.3, overwrite: 'auto' });
                }
            });
            const data = BAR_DATA[index];
            setTooltipContent({ month: data.label, value: data.value });
        } else {
            bar.classList.remove('is-hovered');
            gsap.to(bar, { scaleX: 1, y: 0, duration: 0.3, ease: "power2.out", overwrite: 'auto' });
            barsRef.current.forEach((b) => {
                if (b) gsap.to(b, { opacity: 1, duration: 0.3, overwrite: 'auto' });
            });
        }
    };


    return (
        <section ref={sectionRef} className="w-full bg-slate-50 relative overflow-hidden">

            {/* MOBILE VIEW */}
            <div className="block lg:hidden w-full">
                <MobileBrandVisibilityMetrics />
            </div>

            {/* DESKTOP VIEW */}
            <div ref={containerRef} className="hidden lg:block max-w-7xl mx-auto px-6 py-24 relative z-10 w-full">

                {/* Header */}
                <div className="mb-16">
                    <div ref={labelRef} className="flex items-center gap-2 mb-4">
                        <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></div>
                        <span className="text-blue-600 font-semibold tracking-wide text-sm uppercase">LIVE DATA PULSE</span>
                    </div>
                    <h2 ref={headingRef} className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                        Real-Time Growth Impact
                    </h2>
                    <p ref={subtitleRef} className="text-lg text-slate-500 max-w-2xl">
                        Track your brand's acceleration across all digital touchpoints with our live performance dashboard.
                    </p>
                </div>

                {/* Grid Layout */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 w-full">

                    {/* 1) DONUT CHART */}
                    <div
                        ref={(el) => { cardsRef.current[0] = el; }}
                        className="col-span-1 md:col-span-4 h-[584px] bg-white rounded-[32px] p-8 relative shadow-[0_20px_40px_rgba(0,0,0,0.06)] hover:translate-y-[-6px] hover:shadow-[0_30px_60px_rgba(0,0,0,0.1)] transition-all duration-500 ease-out will-change-transform flex flex-col justify-between"
                    >
                        <div>
                            <h3 className="text-xl font-semibold text-slate-900 mb-1">Traffic Sources</h3>
                            <p className="text-sm text-slate-500">Live breakdown</p>
                        </div>
                        <div className="flex-1 flex flex-col items-center justify-center relative">
                            <div className="relative w-64 h-64">
                                <svg ref={donutRef} viewBox="0 0 200 200" className="w-full h-full -rotate-90">
                                    {donutSegments.map((seg, i) => (
                                        <path
                                            key={i}
                                            ref={(el) => { donutSegmentsRef.current[i] = el; }}
                                            d={seg.d}
                                            fill={seg.color}
                                            stroke="#fff"
                                            strokeWidth="2"
                                            className="hover:opacity-90 transition-opacity cursor-pointer origin-center"
                                        />
                                    ))}
                                    <circle cx="100" cy="100" r="65" fill="white" />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                    <span ref={donutCenterNumRef} className="text-4xl font-bold text-slate-900">0</span>
                                    <span className="text-sm text-slate-500">Total Visits</span>
                                </div>
                            </div>
                        </div>
                        <div ref={donutLabelsRef} className="mt-6 flex flex-col gap-3">
                            {TRAFFIC_DATA.map((item, i) => (
                                <div key={i} className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                                        <span className="text-sm font-medium text-slate-600">{item.label}</span>
                                    </div>
                                    <span className="text-sm font-semibold text-slate-900">{item.value}%</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Col Stack */}
                    <div className="col-span-1 md:col-span-8 flex flex-col gap-6 h-full">

                        {/* ========================================================= */}
                        {/* 2) ORGANIC TRAFFIC GROWTH WIDGET (IMPROVED) */}
                        {/* ========================================================= */}
                        <div
                            ref={(el) => {
                                cardsRef.current[1] = el;
                                barWidgetRef.current = el; // shared ref for specific anim
                            }}
                            className="bg-white rounded-[32px] p-8 h-[320px] shadow-[0_20px_40px_rgba(16,185,129,0.08)] hover:translate-y-[-6px] hover:shadow-[0_30px_60px_rgba(16,185,129,0.12)] transition-all duration-500 ease-out will-change-transform flex flex-col relative"
                        >
                            <div className="flex justify-between items-end mb-8">
                                <div>
                                    <h3 ref={barTitleRef} className="text-lg font-semibold text-slate-900">Organic Traffic Growth</h3>
                                </div>

                                {/* Auto-calculated Badge */}
                                <div ref={barBadgeRef} className={`text-sm font-semibold px-3 py-1 rounded-full ${growthStats.isPositive ? 'bg-emerald-50 text-emerald-500' : 'bg-red-50 text-red-500'}`}>
                                    {growthStats.isPositive ? '+' : ''}{growthStats.percent}% vs last mo
                                </div>
                            </div>

                            {/* Bars Container */}
                            <div className="flex-1 flex items-end justify-between px-4 gap-4 relative">
                                {normalizedBarData.map((bar, i) => (
                                    <div
                                        key={i}
                                        className="group relative flex-1 flex flex-col justify-end items-center h-full outline-none"
                                        onMouseEnter={(e) => handleBarHover(i, true, e)} // Mouse Enter
                                        onMouseLeave={(e) => handleBarHover(i, false, e)} // Mouse Leave
                                        onFocus={(e) => handleBarHover(i, true, e)} // Focus
                                        onBlur={(e) => handleBarHover(i, false, e)} // Blur
                                        tabIndex={0}
                                        aria-label={`${bar.label}: ${bar.value} organic visits`}
                                    >

                                        {/* Value Label (Top of Bar) */}
                                        <span
                                            ref={(el) => { barValuesRef.current[i] = el; }}
                                            className="text-xs font-bold text-emerald-600 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-1 group-hover:translate-y-0"
                                        >
                                            {/* GSAP will animate init value here */}
                                            {bar.value}
                                        </span>

                                        {/* Tooltip (Local to bar for easy positioning) */}
                                        <div
                                            className="absolute bottom-full mb-8 opacity-0 pointer-events-none z-20 transition-all duration-300 ease-out transform translate-y-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 min-w-[100px]"
                                            style={{ left: '50%', transform: 'translateX(-50%)' }} // Default Center
                                        >
                                            {/* Tooltip content */}
                                            <div className={`
                                                bg-slate-800 text-white text-xs py-1.5 px-3 rounded shadow-lg whitespace-nowrap text-center
                                                ${i === 0 ? 'origin-bottom-left translate-x-4' : ''} 
                                                ${i === normalizedBarData.length - 1 ? 'origin-bottom-right -translate-x-4' : ''}
                                                `}
                                            >
                                                <div className="font-bold">{bar.label}</div>
                                                <div>{bar.value} visits</div>
                                                {/* Arrow */}
                                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                                            </div>
                                        </div>

                                        {/* Bar Visual */}
                                        <div className="w-full max-w-[50px] bg-emerald-50 rounded-lg h-full relative overflow-hidden">
                                            <div
                                                ref={(el) => { barsRef.current[i] = el; }}
                                                className="absolute bottom-0 left-0 w-full bg-emerald-500 rounded-lg will-change-transform"
                                                style={{ height: bar.height, transformOrigin: 'bottom' }}
                                            />
                                        </div>

                                        {/* Month Label */}
                                        <span
                                            ref={(el) => { barLabelsRef.current[i] = el; }}
                                            className="text-xs text-slate-400 mt-3 font-medium"
                                        >
                                            {bar.label}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* Disclaimer / Subtle Label */}
                            <div className="absolute top-8 right-8 mt-1 hidden md:block">
                                <span className="text-[10px] text-slate-400 font-medium tracking-wide bg-slate-100 px-2 py-1 rounded-md">Recent performance snapshot</span>
                            </div>
                        </div>

                        {/* Bottom Row */}
                        <div className="grid grid-cols-1 md:grid-cols-8 gap-6 h-[240px]">

                            {/* 3) METRIC CARD (Rank) */}
                            <div
                                ref={(el) => { cardsRef.current[2] = el; }}
                                className="col-span-3 bg-white rounded-[32px] p-8 shadow-[0_20px_40px_rgba(245,158,11,0.08)] hover:translate-y-[-6px] hover:shadow-[0_30px_60px_rgba(245,158,11,0.12)] transition-all duration-500 ease-out will-change-transform flex flex-col justify-between"
                            >
                                <div className="flex justify-between items-start">
                                    <span className="text-slate-500 font-medium leading-tight">Local Pack<br />Presence</span>
                                    <div ref={rankPillRef} className="bg-orange-50 text-orange-600 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide">
                                        Verified
                                    </div>
                                </div>
                                <div className="flex flex-col">
                                    <span ref={rankNumRef} className="text-4xl md:text-5xl font-bold text-orange-500 tracking-tight">Top 3</span>
                                    <span className="text-sm font-bold text-orange-300 uppercase tracking-wide mt-1">Positions</span>
                                </div>
                                <div className="w-full bg-orange-100 h-1.5 rounded-full overflow-hidden mt-2">
                                    <div className="bg-orange-500 h-full w-[95%] rounded-full animate-pulse"></div>
                                </div>
                            </div>

                            {/* 4) LINE CHART (GMB) */}
                            <div
                                ref={(el) => { cardsRef.current[3] = el; }}
                                className="col-span-5 bg-white rounded-[32px] p-6 shadow-[0_20px_40px_rgba(59,130,246,0.08)] hover:translate-y-[-6px] hover:shadow-[0_30px_60px_rgba(59,130,246,0.12)] transition-all duration-500 ease-out will-change-transform relative overflow-hidden"
                            >
                                <div className="absolute top-6 left-6 z-10">
                                    <h3 className="text-lg font-semibold text-slate-900">GMB Profile Views</h3>
                                    <p className="text-sm text-blue-500 font-bold mt-1">+18% this week</p>
                                </div>
                                <div className="absolute bottom-0 left-0 w-full h-[70%]">
                                    <svg viewBox="0 0 500 150" className="w-full h-full preserve-3d">
                                        <defs>
                                            <linearGradient id="lineGap" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.2" />
                                                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                                            </linearGradient>
                                        </defs>
                                        <path
                                            ref={lineAreaRef}
                                            d={lineChartPaths.area}
                                            fill="url(#lineGap)"
                                        />
                                        <path
                                            ref={linePathRef}
                                            d={lineChartPaths.line}
                                            fill="none"
                                            stroke="#3b82f6"
                                            strokeWidth="4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <circle
                                            ref={pulseDotRef}
                                            cx="500"
                                            cy={150 - ((360 / 400) * 150)} // Scale updated: 360/400
                                            r="6"
                                            fill="#3b82f6"
                                            stroke="#fff"
                                            strokeWidth="3"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ETHICAL DISCLAIMER */}
                <div className="w-full text-center mt-12 opacity-40 hover:opacity-100 transition-opacity duration-300">
                    <p className="text-[10px] uppercase tracking-widest text-slate-500">
                        Metrics shown represent sample growth patterns based on real client outcomes.
                    </p>
                </div>

            </div>
        </section>
    );
};

export default GrowthImpactSection;